import type { EventLot, EventSummary, Money } from '@/types'

const fmt = (tz: string, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-US', { timeZone: tz, ...o })
const DAY = 24 * 60 * 60 * 1000

/** "$20", "$7.50". */
export function money(m: Money): string {
  const n = Number(m.amount)
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: m.currency,
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
  }).format(n)
}

/** "7:30 PM" in the event's own time zone. */
export function clock(iso: string, tz: string): string {
  return fmt(tz, { hour: 'numeric', minute: '2-digit' }).format(new Date(iso))
}

/** "Fri, Oct 23 · 7:30 PM". */
export function when(e: EventSummary): string {
  return `${fmt(e.timezone, { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(e.startsAt))} · ${clock(e.startsAt, e.timezone)}`
}

/** For a screen reader: "Friday, October 23 at 7:30 PM". */
export function whenSpoken(e: EventSummary): string {
  return `${fmt(e.timezone, { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date(e.startsAt))} at ${clock(e.startsAt, e.timezone)}`
}

/** The date tile in a list: "FRI" over "23". */
export function dateTile(e: EventSummary): { weekday: string; day: string } {
  const at = new Date(e.startsAt)
  return {
    weekday: fmt(e.timezone, { weekday: 'short' }).format(at).toUpperCase(),
    day: fmt(e.timezone, { day: 'numeric' }).format(at),
  }
}

/** On the pin: "Today 7:30", "Fri 7:30" within the week, "Oct 30" further out. */
export function pinLabel(e: EventSummary, now = new Date()): string {
  if (e.state === 'live') return 'Now'
  const at = new Date(e.startsAt)
  const time = clock(e.startsAt, e.timezone).replace(/\s?[AP]M$/, '')
  const day = (d: Date) => fmt(e.timezone, { dateStyle: 'short' }).format(d)
  if (day(at) === day(now)) return `Today ${time}`
  if (at.getTime() - now.getTime() < 6 * DAY) return `${fmt(e.timezone, { weekday: 'short' }).format(at)} ${time}`
  return fmt(e.timezone, { month: 'short', day: 'numeric' }).format(at)
}

export type Tone = 'ok' | 'low' | 'out' | 'muted'

/** What an event's parking looks like now, for its chip and its pin. */
export function eventStatus(e: EventSummary): { label: string; tone: Tone } {
  switch (e.state) {
    case 'canceled': return { label: 'Canceled', tone: 'out' }
    case 'ended': return { label: 'Ended', tone: 'muted' }
    case 'sold_out': return { label: 'Sold out', tone: 'out' }
    case 'live': return { label: 'Happening now', tone: 'ok' }
    case 'not_on_sale': return { label: e.priceFrom ? 'Pay on arrival' : 'Not on sale yet', tone: 'muted' }
    case 'on_sale':
      return e.remaining !== null && e.remaining <= 25
        ? { label: 'Selling out', tone: 'low' }
        : { label: 'On sale', tone: 'ok' }
  }
}

/** A lot's availability: "Selling out · 9 left", "Sold out", "Pay on arrival". */
export function lotStatus(l: EventLot): { label: string; tone: Tone } {
  if (l.sale === 'elsewhere') return { label: 'Sold by partner', tone: 'muted' }
  if (l.sale === 'drive_up') return { label: l.liveFree !== null ? `Pay on arrival · ${l.liveFree} free now` : 'Pay on arrival', tone: 'muted' }
  if (l.soldOut) return { label: 'Sold out', tone: 'out' }
  if (!l.onSale) return { label: 'Not on sale yet', tone: 'muted' }
  if (l.remaining !== null && l.remaining <= Math.max(10, (l.inventory ?? 0) * 0.15)) {
    return { label: `Selling out · ${l.remaining} left`, tone: 'low' }
  }
  return { label: l.remaining !== null ? `${l.remaining} left` : 'Available', tone: 'ok' }
}

/** Text and fill for each tone, readable in light and dark. */
export const TONE_TEXT: Record<Tone, string> = {
  ok: 'text-emerald-700 dark:text-emerald-400',
  low: 'text-amber-700 dark:text-amber-400',
  out: 'text-red-700 dark:text-red-400',
  muted: 'text-muted-foreground',
}
export const TONE_CHIP: Record<Tone, string> = {
  ok: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  low: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',
  out: 'bg-red-500/10 text-red-700 dark:text-red-300',
  muted: 'bg-muted text-muted-foreground',
}
